/**
 * Watches the stores while a Kriegsakademie lesson is open: captures the step baseline, checks
 * the step's condition on every change, marks it done (stamp + sound) and advances after the
 * player had a moment to read. Mounted by the instructor card inside the game screen.
 */
import { useEffect } from 'react';
import { bus } from '../game/bus';
import { gameStore } from '../game/store';
import { type CameraPos, captureBaseline, TUTORIAL_STEPS, type TutorialSnapshot } from './steps';
import { tutorialStore } from './store';

/** A finished task stays on screen at least this long before the next lesson opens (ms). */
export const DONE_HOLD_MS = 1500;
/** …and no lesson is left before it was on screen this long (ms). */
export const MIN_READ_MS = 3500;

let camera: CameraPos | null = null;

/** The current world as the conditions see it, or null before the first view. */
export function tutorialSnapshot(): TutorialSnapshot | null {
  const g = gameStore.getState();
  if (!g.view || !g.grid) return null;
  return {
    view: g.view,
    grid: g.grid,
    selection: g.selection,
    inspectedContact: g.inspectedContact,
    paused: g.paused,
    camera,
    log: g.log,
  };
}

function evaluate(): void {
  const t = tutorialStore.getState();
  if (!t.active || t.doneStep === t.step) return;
  const snap = tutorialSnapshot();
  if (!snap) return;
  if (!t.baseline) {
    t.setBaseline(captureBaseline(snap));
    return;
  }
  // The renderer reports its camera a moment after the map mounts; adopt the first report.
  if (!t.baseline.camera && snap.camera) {
    t.setBaseline({ ...t.baseline, camera: snap.camera });
    return;
  }
  const done = TUTORIAL_STEPS[t.step]?.done;
  if (done?.(snap, t.baseline)) {
    t.markDone(t.step);
    bus.emit('sound', { name: 'stamp', volume: 0.7 });
  }
}

export function useTutorialDriver(): void {
  useEffect(() => {
    const offs = [
      bus.on('camera', (c) => {
        camera = { x: c.x + c.width / 2, y: c.y + c.height / 2, width: c.width, zoom: c.zoom };
        evaluate();
      }),
      gameStore.subscribe(evaluate),
      tutorialStore.subscribe((s, p) => {
        if (s.step !== p.step || s.active !== p.active || s.baseline !== p.baseline) evaluate();
      }),
    ];
    evaluate();
    return () => {
      for (const off of offs) off();
    };
  }, []);

  // Auto-advance once a task is done (after the stamp and the minimum reading time).
  useEffect(
    () =>
      tutorialStore.subscribe((s, p) => {
        if (s.doneStep === p.doneStep || s.doneStep !== s.step) return;
        const step = s.step;
        const wait = Math.max(DONE_HOLD_MS, MIN_READ_MS - (performance.now() - s.openedAt));
        window.setTimeout(() => {
          const now = tutorialStore.getState();
          if (now.active && now.step === step && now.doneStep === step) now.next();
        }, wait);
      }),
    [],
  );
}
